import { FunctionComponent, useEffect, useState } from 'react'
import { sortBySuggestPriority } from '../suggestTableUtils'
import styles from './suggestStaffTable.module.scss'
import Indicator from '@/components/_custom/Indicator/indicator'
import Desktop from '@/modules/desktopMobile/desktop'
import Title from '@/components/_custom/Title/title'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import Mobile from '@/modules/desktopMobile/mobile'
import { useAuth } from '@/modules/context/AuthContext'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { GridColDef } from '@mui/x-data-grid'
import { Close, Favorite, FavoriteBorder, LinkSharp } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import {
    GET_DISLIKED_SUGGEST_STAFFS,
    GET_SUGGEST_STAFFS,
    SET_DISLIKE_SUGGEST,
    SET_LIKE_SUGGEST
} from '@/modules/API/suggest/suggestHandler'
import CALDStaffSettings from './CALDStaffSettings'
import { Role } from '@/types/Role'
import EditIcon from '@mui/icons-material/Edit'
import ConfirmAssociateStaffDialog from './Dialog/confirmAssociateStaffDialog'
import EditInternalNoteDialog from '../Dialog/editInternalNoteDialog'
import { SuggestStaffProfile } from '@/types/Suggest'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

type DataTable = {
    id: number
    userId?: number
    name: string
    email: string
    phone: string
    city: string
    internalNote?: string
    liked: boolean
    disliked: boolean
}

const SuggestStaffTable: FunctionComponent = () => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [allSuggest, setAllSuggest] = useState<SuggestStaffProfile[]>([])
    const [countLikedSuggest, setCountLikedSuggest] = useState<number>(0)
    const [countCommentedSuggest, setCountCommentedSuggest] = useState<number>(0)

    const [tableData, setTableData] = useState<DataTable[]>([])

    const [isInternalNoteDialogOpen, setInternalNoteDialogOpen] = useState(false)
    const [userIdAndInternalNote, setUserIdAndInternalNote] = useState<
        { userId: number; internalNote: string } | undefined
    >(undefined)

    const [isConfirmAssociateDialogOpen, setConfirmAssociateDialogOpen] = useState(false)
    const [emailToAssociate, setEmailToAssociate] = useState<string>('')
    const [nameToAssociate, setNameToAssociate] = useState<string>('')

    const [CALDRequest, setCALDRequest] = useState<boolean>(false)

    const [showMasked, setShowMasked] = useState<boolean>(false)
    const [maskedData, setMaskedData] = useState<SuggestStaffProfile[]>([])

    const canEditSuggest: boolean = user?.currentDaycareRights.canWriteSuggestions || false

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const {
        loading: loadingGetSuggestStaff,
        data: dataGetSuggestStaff,
        error: errorGetSuggestStaff,
        refetch: refetchGetSuggestStaff
    } = useQuery(GET_SUGGEST_STAFFS, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataGetSuggestStaff) {
            setCALDRequest(false)
            setAllSuggest([...dataGetSuggestStaff.getSuggestStaffs].sort(sortBySuggestPriority))
        }
    }, [dataGetSuggestStaff])

    useEffect(() => {
        if (errorGetSuggestStaff) {
            setAllSuggest([])
        }
    }, [errorGetSuggestStaff])

    const [loadingRefetchStaff, setLoadingRefetchStaff] = useState(false)

    async function handleRefetch(): Promise<void> {
        setLoadingRefetchStaff(true)

        const { data } = await refetchGetSuggestStaff()

        if (data) {
            setAllSuggest([...data.getSuggestStaffs].sort(sortBySuggestPriority))
        }

        setLoadingRefetchStaff(false)
    }

    const [getDislikedStaffs, { loading: loadingDisliked }] = useLazyQuery(GET_DISLIKED_SUGGEST_STAFFS, {
        fetchPolicy: 'network-only',
        variables: { daycareId: user?.currentDaycare?.id },
        onCompleted: (data: { getDislikedSuggestStaffs: SuggestStaffProfile[] }) =>
            setMaskedData(data.getDislikedSuggestStaffs)
    })

    const [setDislikeSuggestMutation] = useMutation(SET_DISLIKE_SUGGEST, {
        onError: () => showNotificationError(t('suggestLikedError'))
    })

    const [likeSuggest] = useMutation(SET_LIKE_SUGGEST, {
        onCompleted: () => {
            handleRefetch()

            showNotificationSuccess(t('suggestLiked'))
        },
        onError: () => {
            showNotificationError(t('suggestLikedError'))
        }
    })

    useEffect(() => {
        if (allSuggest.length > 0) {
            setCountLikedSuggest(getCountLikedSuggest(allSuggest))
            setCountCommentedSuggest(getCountCommentedSuggest(allSuggest))
            setTableData(getTableData(allSuggest))
        } else {
            setCountLikedSuggest(0)
            setCountCommentedSuggest(0)
            setTableData([])
        }
    }, [allSuggest])

    async function handleDislike(userId: number, currentDisliked: boolean): Promise<void> {
        if (!canEditSuggest) return
        await setDislikeSuggestMutation({
            variables: {
                userId,
                daycareId: user?.currentDaycare?.id,
                disliked: !currentDisliked
            }
        })
        if (!currentDisliked) {
            const profile: SuggestStaffProfile | undefined = allSuggest.find((s) => s.staff.userId === userId)
            setTableData((prev) => prev.filter((row) => row.userId !== userId))
            setAllSuggest((prev: SuggestStaffProfile[]) => prev.filter((s) => s.staff.userId !== userId))
            if (profile) setMaskedData((prev) => [...prev, { ...profile, disliked: true }])
        } else {
            const profile: SuggestStaffProfile | undefined = maskedData.find((s) => s.staff.userId === userId)
            setMaskedData((prev) => prev.filter((s) => s.staff.userId !== userId))
            if (profile) setAllSuggest((prev) => [...prev, { ...profile, disliked: false }])
        }
    }

    async function handleLike(userId: number, liked: boolean): Promise<void> {
        if (canEditSuggest) {
            await likeSuggest({
                variables: {
                    userId: userId,
                    daycareId: user?.currentDaycare?.id,
                    liked: !liked
                }
            })
        }
    }

    const columnsWeb: GridColDef[] = [
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <a
                    href={`/adults?parent=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/adults?parent=${params.row.id}`)
                        }
                    }}
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.city ? params.row.city : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'internalNote',
            headerName: t('internalNote'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.CommentContainer} ${canEditSuggest ? styles.Clickable : ''}`}
                    onClick={() => openInternalNoteDialog(params.row.userId, params.row.internalNote)}
                >
                    <EditIcon className={canEditSuggest ? styles.CommentIcon : styles.CommentIconWithoutHover} />
                    <p className={styles.Comment}>{params.row.internalNote ? params.row.internalNote : ''}</p>
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a onClick={() => handleLike(params.row.userId, params.row.liked)} className={styles.Link}>
                        {params.row.liked ? (
                            <Favorite className={canEditSuggest ? styles.HeartIcon : styles.HeartIconWithoutHover} />
                        ) : (
                            <FavoriteBorder className={canEditSuggest ? styles.Icon : styles.IconWithoutHover} />
                        )}
                    </a>
                    {canEditSuggest && !showMasked && (
                        <a
                            onClick={() => handleDislike(params.row.userId, params.row.disliked)}
                            className={styles.Link}
                        >
                            <ThumbDownAltIcon className={params.row.disliked ? styles.ActiveIcon : styles.Icon} />
                        </a>
                    )}
                    {showMasked && (
                        <a onClick={() => handleDislike(params.row.userId, true)} className={styles.Link}>
                            <ThumbUpAltIcon className={styles.Icon} />
                        </a>
                    )}
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {canEditSuggest && user?.currentDaycareRights.canWriteStaffRights && (
                        <a
                            onClick={() => {
                                setEmailToAssociate(params.row.email)
                                setNameToAssociate(params.row.name)
                                setConfirmAssociateDialogOpen(true)
                            }}
                            className={styles.Link}
                        >
                            <LinkSharp className={styles.Icon} />
                        </a>
                    )}
                </div>
            )
        }
    ]

    const columnsWebCALD: GridColDef[] = [
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <a
                    href={`/adults?parent=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/adults?parent=${params.row.id}`)
                        }
                    }}
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.city ? params.row.city : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const columnsMobile: GridColDef[] = [
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 100,
            minWidth: 100,
            maxWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 120,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a onClick={() => handleLike(params.row.userId, params.row.liked)} className={styles.Link}>
                        {params.row.liked ? (
                            <Favorite className={styles.HeartIcon} />
                        ) : (
                            <FavoriteBorder className={styles.Icon} />
                        )}
                    </a>
                    {canEditSuggest && !showMasked && (
                        <a
                            onClick={() => handleDislike(params.row.userId, params.row.disliked)}
                            className={styles.Link}
                        >
                            <ThumbDownAltIcon className={params.row.disliked ? styles.ActiveIcon : styles.Icon} />
                        </a>
                    )}
                    {showMasked && (
                        <a onClick={() => handleDislike(params.row.userId, true)} className={styles.Link}>
                            <ThumbUpAltIcon className={styles.Icon} />
                        </a>
                    )}
                    <a
                        href={`/staff?profile=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/staff?profile=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {canEditSuggest && user?.currentDaycareRights.canWriteStaffRights && (
                        <a
                            onClick={() => {
                                setEmailToAssociate(params.row.email)
                                setNameToAssociate(params.row.name)
                                setConfirmAssociateDialogOpen(true)
                            }}
                            className={styles.Link}
                        >
                            <LinkSharp className={styles.Icon} />
                        </a>
                    )}
                </div>
            )
        }
    ]

    function openInternalNoteDialog(userId: number, internalNote: string): void {
        if (canEditSuggest) {
            setUserIdAndInternalNote({
                userId: userId,
                internalNote: internalNote
            })
            setInternalNoteDialogOpen(true)
        }
    }

    function getTableData(suggests: SuggestStaffProfile[]): DataTable[] {
        return suggests.map((suggest: SuggestStaffProfile) => {
            return {
                id: suggest.staff.id,
                userId: suggest.staff.userId,
                name: suggest.staff.lastname + ' ' + suggest.staff.firstname,
                city: suggest.staff.city,
                email: suggest.staff.email,
                phone: suggest.staff.phone,
                internalNote: suggest.internalNote,
                liked: suggest.liked || false,
                disliked: suggest.disliked || false
            }
        })
    }

    function resetFilter(): void {
        setShowMasked(false)
        setTableData(getTableData(allSuggest))
    }

    function filterMasked(): void {
        setShowMasked(true)
        getDislikedStaffs()
    }

    function filterLiked(): void {
        setTableData(getTableData(allSuggest).filter((row) => row.liked))
    }

    function getCountLikedSuggest(suggests: SuggestStaffProfile[]): number {
        return suggests.filter((suggest) => suggest.liked).length
    }

    function filterCommented(): void {
        setTableData(getTableData(allSuggest).filter((row) => row.internalNote !== ''))
    }

    function getCountCommentedSuggest(suggests: SuggestStaffProfile[]): number {
        return suggests.filter((suggest) => suggest.internalNote !== '').length
    }

    const displayData: DataTable[] = showMasked ? getTableData(maskedData) : tableData

    return (
        <>
            {user?.role === Role.cald && user.currentDaycare && (
                <CALDStaffSettings
                    setAllSuggest={setAllSuggest}
                    daycare={user.currentDaycare}
                    setCALDRequest={setCALDRequest}
                />
            )}
            <div className={styles.IndicatorsContainer}>
                <a onClick={resetFilter} className={styles.Clickable}>
                    <Indicator count={allSuggest.length} backgroundWhite text={t('all')} />
                </a>
                <a onClick={filterLiked} className={styles.Clickable}>
                    <Indicator count={countLikedSuggest} backgroundWhite text={t('liked')} />
                </a>
                {countCommentedSuggest > 0 && (
                    <a onClick={filterCommented} className={styles.Clickable}>
                        <Indicator count={countCommentedSuggest} backgroundWhite text={t('comment')} />
                    </a>
                )}
                {canEditSuggest && (
                    <a onClick={showMasked ? resetFilter : filterMasked} className={styles.Clickable}>
                        <Indicator count={maskedData.length} backgroundWhite text={t('masked')} />
                    </a>
                )}
            </div>
            {(loadingGetSuggestStaff || loadingRefetchStaff || loadingDisliked) && <Loading overall />}
            {(dataGetSuggestStaff || user?.role === Role.cald) && (
                <div className={styles.DataGridContainer}>
                    <div className={styles.Title}>
                        <Title text={t('mySuggests') + ' - ' + t('staffs')} />
                    </div>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={CALDRequest ? columnsWebCALD : columnsWeb}
                            rows={displayData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            initialState={{
                                filter: {
                                    filterModel: {
                                        items: [],
                                        quickFilterExcludeHiddenColumns: false
                                    }
                                }
                            }}
                            rowCount={displayData.length}
                            paginationMode={'server'}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={columnsMobile}
                            rows={displayData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            initialState={{
                                filter: {
                                    filterModel: {
                                        items: [],
                                        quickFilterExcludeHiddenColumns: false
                                    }
                                }
                            }}
                        />
                    </Mobile>
                </div>
            )}
            {errorGetSuggestStaff && user?.role !== Role.cald && <ErrorMessage error={t('generalError')} />}
            {isConfirmAssociateDialogOpen && emailToAssociate !== '' && (
                <ConfirmAssociateStaffDialog
                    open={isConfirmAssociateDialogOpen}
                    closeDialog={() => setConfirmAssociateDialogOpen(false)}
                    name={nameToAssociate}
                    email={emailToAssociate}
                    refetchAllSuggest={handleRefetch}
                />
            )}
            {isInternalNoteDialogOpen && userIdAndInternalNote && (
                <EditInternalNoteDialog
                    open={isInternalNoteDialogOpen}
                    closeDialog={() => setInternalNoteDialogOpen(false)}
                    internalNote={userIdAndInternalNote.internalNote}
                    userId={userIdAndInternalNote.userId}
                    refetchAllSuggest={handleRefetch}
                />
            )}
        </>
    )
}

export default SuggestStaffTable
